import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { axiosInstance } from '../../services/axiosInstance';
import MonitoringPoolAssignField from './MonitoringPoolAssignField';

interface Admin {
  id: number;
  username: string;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  is_superuser: boolean;
  is_main_admin: boolean;
  monitor_all_pools?: boolean;
  last_login: string;
  monitoring_pool_ids?: number[];
}

interface EditAdminDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  admin: Admin | null;
}

const EditAdminDialog: React.FC<EditAdminDialogProps> = ({
  open,
  onClose,
  onSuccess,
  admin
}) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    name: '',
    password: '',
    confirmPassword: '',
    is_superuser: false,
    is_active: true,
    is_main_admin: false
  });
  const [monitoringPoolIds, setMonitoringPoolIds] = useState<number[]>([]);
  const [monitorAllPools, setMonitorAllPools] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (admin) {
      setFormData({
        username: admin.username || '',
        email: admin.email || '',
        name: admin.name || '',
        password: '',
        confirmPassword: '',
        is_superuser: admin.is_superuser || false,
        is_active: admin.is_active || true,
        is_main_admin: admin.is_main_admin || false
      });
      const all = Boolean(admin.monitor_all_pools);
      setMonitorAllPools(all);
      const ids = Array.isArray(admin.monitoring_pool_ids)
        ? admin.monitoring_pool_ids.map(Number)
        : [];
      setMonitoringPoolIds(all ? [] : ids);
      if (admin.id) {
        void axiosInstance
          .get<{ pool_ids?: number[]; monitor_all_pools?: boolean }>(
            `/api/users/${admin.id}/monitoring-pools`
          )
          .then((res) => {
            const resAll = Boolean(res.data?.monitor_all_pools);
            setMonitorAllPools(resAll);
            setMonitoringPoolIds(
              resAll
                ? []
                : Array.isArray(res.data?.pool_ids)
                  ? res.data.pool_ids.map(Number)
                  : []
            );
          })
          .catch(() => {
            /* ignore */
          });
      }
    }
  }, [admin]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'is_superuser' || name === 'is_active' || name === 'is_main_admin' ? checked : value
    }));
    if (name === 'is_superuser' && checked) {
      setMonitoringPoolIds([]);
      setMonitorAllPools(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!admin) return;

    // 입력값 검증
    if (!formData.username || !formData.email) {
      setError('필수 항목을 입력');
      return;
    }

    // 비밀번호가 입력된 경우에만 검증
    if (formData.password) {
      if (formData.password !== formData.confirmPassword) {
        setError('비밀번호가 일치하지 않음');
        return;
      }
      if (formData.password.length < 8) {
        setError('비밀번호는 8자리 이상');
        return;
      }
    }

    setLoading(true);

    try {
      const updateData: Record<string, unknown> = {
        username: formData.username.trim(),
        email: formData.email.trim(),
        full_name: formData.name.trim(),
        is_superuser: formData.is_superuser,
        is_active: formData.is_active,
        is_main_admin: formData.is_main_admin
      };

      // 비밀번호가 입력된 경우에만 포함
      if (formData.password) {
        updateData.password = formData.password;
      }

      await axiosInstance.put(`/api/users/${admin.id}`, updateData);

      if (!formData.is_superuser) {
        await axiosInstance.put(`/api/users/${admin.id}/monitoring-pools`, {
          pool_ids: monitorAllPools ? [] : monitoringPoolIds,
          monitor_all_pools: monitorAllPools,
        });
      } else {
        await axiosInstance.put(`/api/users/${admin.id}/monitoring-pools`, {
          pool_ids: [],
          monitor_all_pools: false,
        });
      }

      enqueueSnackbar('관리자 정보가 수정됨', { variant: 'success' });
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('관리자 수정 오류:', error);
      if (error.response?.status === 401) {
        setError('인증이 만료. 다시 로그인');
        window.location.href = '/login';
      } else {
        const detail = error.response?.data?.detail;
        let errorMessage = '관리자 수정 중 오류 발생';
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail
            .map((d: { msg?: string }) => d?.msg)
            .filter(Boolean)
            .join(' ') || errorMessage;
        } else if (typeof error.response?.data === 'string') {
          errorMessage = error.response.data;
        }
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>관리자 정보 수정</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="아이디"
            name="username"
            value={formData.username}
            onChange={handleChange}
            margin="normal"
            required
            disabled={loading}
          />
          <TextField
            fullWidth
            label="이메일"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            margin="normal"
            required
            disabled={loading}
          />
          <TextField
            fullWidth
            label="이름"
            name="name"
            value={formData.name}
            onChange={handleChange}
            margin="normal"
            disabled={loading}
          />
          <TextField
            fullWidth
            label="비밀번호"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            margin="normal"
            disabled={loading}
            inputProps={{ minLength: 8 }}
            helperText="변경 없으면 비움. 8자리 이상"
          />
          <TextField
            fullWidth
            label="비밀번호 확인"
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
            margin="normal"
            disabled={loading}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_superuser}
                onChange={handleChange}
                name="is_superuser"
                disabled={loading}
              />
            }
            label="최고관리자 권한"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_main_admin}
                onChange={handleChange}
                name="is_main_admin"
                disabled={loading}
              />
            }
            label="메인 관리자"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={handleChange}
                name="is_active"
                disabled={loading}
              />
            }
            label="계정 활성화"
          />
          <MonitoringPoolAssignField
            adminId={admin?.id ?? null}
            selectedPoolIds={monitoringPoolIds}
            onChange={setMonitoringPoolIds}
            monitorAllPools={monitorAllPools}
            onMonitorAllPoolsChange={setMonitorAllPools}
            disabled={loading}
            hidden={formData.is_superuser}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            취소
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
          >
            수정
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditAdminDialog; 